import Comment from "./Comment";
import { OfferSignMethod } from "./OfferTemplate";
import UploadedFile from "./UploadedFile";
import { createTemporaryId, isCreated } from "../utils/model";
import Issuer from "./Issuer";
import { getProductTotal, ProductReference, ProductTotal } from "./ProductReference";
import { BasicFieldReference } from "./BasicField";
import { OfferStatusLinks } from "./OfferStatusLinks";
import { Country } from "./Country";
import { text } from "../utils/i18n";
import { OfferTemplateFieldType, OfferTemplateInsert, OfferTemplateInsertType } from "./OfferTemplateInsert";
import { OrderPart } from "./OrderPart";
import { eq } from "../utils";
import User from "./User";
import { UserRightsOffers } from "s2-rights";
import { OfferReview } from "./OfferReview";

export class OfferAttachedFile extends UploadedFile {
  readonly page?: number;
  readonly pageCount?: number;
}

export enum OfferUnsignedDocumentStatus {
  Waiting = 'waiting',
  Ready = 'ready',
  Error = 'error'
}

export type AdditionalPageReference = {
  id: string;
}

/**
 * Represents an offer.
 */
export default class Offer {
  readonly id: string;
  readonly key = createTemporaryId();
  readonly serialId: number;
  readonly publicId: string = null;
  readonly templateId: string;
  /**
   * ID of an offer which this was copied from.
   */
  readonly parentId?: string;
  readonly inserts: OfferInsertReference[] = [];
  readonly templateInserts: OfferTemplateInsert[] = [];
  readonly date: Date;
  readonly recipient?: OfferMainRecipient;
  readonly signature?: OfferPreviousSignature;
  readonly read?: OfferRead;
  readonly distributions: OfferDistribution[] = [];
  readonly comments: Comment[] = [];
  readonly files: OfferAttachedFile[] = [];
  readonly status: OfferStatus = OfferStatus.Pending;
  readonly products: ProductReference[] = [];
  readonly issuer = new Issuer();
  readonly userId: string;
  readonly contactId?: string;
  readonly orderIds: string[];
  readonly decline?: OfferDecline;
  readonly cancellation?: OfferCancellation;
  readonly reviewPending?: OfferReviewPending;
  readonly isTest?: boolean;
  readonly expireAt?: Date;
  readonly bankIdSession?: OfferBankIDSignSession;
  readonly country: Country = Country.Sweden;
  readonly fields: BasicFieldReference[] = []; // Field references from insert references will be automatically created if undefined.
  /**
   * Optional list of references which the user typed themselves.
   * Should override `fields` when default values are set.
   */
  readonly inputFields?: BasicFieldReference[];
  readonly createdFrom?: string;
  readonly total: ProductTotal;
  readonly statusLinks?: OfferStatusLinks;
  readonly projectId?: string = null;
  readonly unsignedDocument?: { url?: string; status?: OfferUnsignedDocumentStatus; };
  readonly additionalPages?: AdditionalPageReference[];

  /**
   * Overridden sign method. Should default to template's sign method.
   */
  readonly signMethod?: OfferSignMethod;

  /**
   * Signature place set on creation.
   */
  readonly signaturePlace?: string;
  readonly parts: OrderPart[] = [];
  readonly reviews: OfferReview[] = [];
  readonly callId?: string;
  readonly dialGroupId?: string;
  readonly shuffleDialGroupId?: string;
  readonly chatToken?: string;
  readonly chatSessionId?: string;
  readonly formId?: string;
  private fieldValues: { [fieldId: string]: string } = null;

  constructor(deriveFrom?: Partial<Offer>) {
    if (deriveFrom) {
      const _deriveFrom = deriveFrom as any;
      delete _deriveFrom.isCanceled;
      delete _deriveFrom.isDistributed;
      delete _deriveFrom.isExpired;

      Object.assign(this, deriveFrom);
    }

    if (this.cancellation) {
      this.cancellation = new OfferCancellation(this.cancellation);
    }

    if (this.reviewPending) {
      this.reviewPending = new OfferReviewPending(this.reviewPending);
    }

    if (this.date) {
      this.date = new Date(this.date);
    }

    if (this.signature) {
      this.signature = new OfferPreviousSignature(this.signature);
    }

    if (this.recipient) {
      this.recipient = new OfferMainRecipient(this.recipient);
    }

    if (this.read) {
      this.read = new OfferRead(this.read);
    }

    if (this.distributions) {
      this.distributions = this.distributions.map(distribution =>
        new OfferDistribution(distribution));
    }

    if (this.comments) {
      this.comments = this.comments.map(comment => new Comment(comment));
    }

    if (this.expireAt) {
      this.expireAt = new Date(this.expireAt);
    }

    this.fields = this.fields?.map(field => new BasicFieldReference(field));

    if (this.products) {
      this.products = this.products.map(reference => new ProductReference(reference));
    }

    const summedProducts = [...this.products];

    if (this.statusLinks) {
      this.statusLinks = new OfferStatusLinks(this.statusLinks);
    }

    this.total = getProductTotal(summedProducts);

    if (this.templateInserts) {
      this.templateInserts = this.templateInserts.map(insert => new OfferTemplateInsert(insert));
    }

    this.parts = this.parts.map(part => new OrderPart(part));
    this.reviews = this.reviews.map(review => new OfferReview(review));
  }

  get isEditable() {
    return this.status === OfferStatus.Pending || !this.isCreated;
  }

  get isCreated() {
    return isCreated(this.id);
  }

  /**
   * @returns Whether this offer has been signed.
   */
  get isSigned() {
    return !!this.signature;
  }

  /**
   * @returns Whether this offer has been read.
   */
  get isRead() {
    return !!this.read;
  }

  /**
   * @returns Whether this offer has been distributed.
   */
  get isDistributed() {
    return this.distributions?.length > 0;
  }

  /**
   * @returns Whether this offer has been canceled.
   */
  get isCanceled() {
    return this.cancellation != null;
  }

  get attachments() {
    return this.files.filter(file => typeof file.page === 'number');
  }

  canBeReviewed(user: User): boolean {
    if (!user.hasRight(UserRightsOffers.OffersReviewAll)) {
      return false;
    }

    return this.status === OfferStatus.ReviewPending || Boolean(
      this.status === OfferStatus.Distributed &&
      this.reviews.find(review => review.userId === user.id)
    );
  }

  /**
   * @param fieldId Order field ID.
   * @returns A field value given a field ID.
   */
  getFieldValue(fieldId: string): string | undefined {
    if (!this.templateInserts) {
      return null;
    }

    if (this.fieldValues) {
      return this.fieldValues[fieldId];
    }

    this.fieldValues = {};

    //  TODO: if the template's inserts changes, then this cache must be invalidated.
    for (const insert of this.templateInserts) {
      if (!insert.fieldId) {
        continue;
      }

      this.fieldValues[insert.fieldId] =
        this.inserts.find(insertReference => insertReference.insertId === insert.id)?.value;
    }

    return this.fieldValues[fieldId];
  }

  /**
   * Returns the number of signatures in the document.
   */
  getSignatureCount() {
    return this.templateInserts.filter(insert => insert.type === OfferTemplateInsertType.Signature).length;
  }

  /**
   * Returns a list of order field IDs which can be input.
   */
  listInputableFieldIds() {
    return this.templateInserts.reduce((fieldIds, insert) => {
      if (insert.fieldType === OfferTemplateFieldType.OrderField &&
        insert.fieldId &&
        !fieldIds.includes(insert.fieldId)) {

        fieldIds.push(insert.fieldId);
      }

      return fieldIds;
    }, [] as string[]);
  }

  /**
   * @returns Whether the user has made changes to this offer when compared to `original`.
   */
  hasUserMadeChanges(original: Offer) {
    //  Fixes typing and erasing a field.
    const fields = this.fields?.filter(reference => reference.value);
    const originalFields = original.fields?.filter(reference => reference.value);

    return (
      !eq(fields, originalFields) ||
      !eq(this.products, original.products) ||
      this.templateId !== original.templateId
    );
  }

  copy(): Partial<Offer> {
    return {
      fields: this.fields,
      products: this.products,
      templateId: this.templateId,
      inserts: this.inserts,
      templateInserts: this.templateInserts,
      recipient: this.recipient,
      contactId: this.contactId,
      callId: this.callId,
      dialGroupId: this.dialGroupId,
      shuffleDialGroupId: this.shuffleDialGroupId,
      parts: this.parts,
      signaturePlace: this.signaturePlace,
      country: this.country,
      projectId: this.projectId,
      additionalPages: this.additionalPages,
      signMethod: this.signMethod,
      parentId: this.id,
    };
  }
}

export class OfferBankIDSignSession {
  id: string;
  status: string;
  extendedStatus?: string;
}

export class OfferCancellation {
  canceledAt: Date;

  constructor(deriveFrom?: Partial<OfferCancellation>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.canceledAt) {
      this.canceledAt = new Date(this.canceledAt);
    }
  }
}

export class OfferReviewPending {
  readonly signedAt: Date;
  readonly reviewPendingAt: Date;

  constructor(deriveFrom?: Partial<OfferReviewPending>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.signedAt) {
      this.signedAt = new Date(this.signedAt);
    }

    if (this.reviewPendingAt) {
      this.reviewPendingAt = new Date(this.reviewPendingAt);
    }
  }
}

export class OfferPreviousSignature {
  signedAt: Date;
  signedDocument: {
    url: string
  };

  constructor(deriveFrom?: Partial<OfferPreviousSignature>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.signedAt) {
      this.signedAt = new Date(this.signedAt);
    }
  }
}

export class OfferRead {
  readAt: Date;

  constructor(deriveFrom?: Partial<OfferRead>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.readAt) {
      this.readAt = new Date(this.readAt);
    }
  }
}

export class OfferDecline {
  declinedAt: Date;
  message?: string;

  constructor(deriveFrom?: Partial<OfferRead>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.declinedAt) {
      this.declinedAt = new Date(this.declinedAt);
    }
  }
}

export class OfferDistribution {
  constructor(deriveFrom?: Partial<OfferDistribution>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }
  }
}

export class OfferMainRecipient {
  name?: string;
  sms?: string;
  email?: string;

  constructor(deriveFrom?: Partial<OfferMainRecipient>) {
    if (!deriveFrom) {
      return;
    }

    Object.assign(this, deriveFrom);

    //  Required for backend validators
    this.sms = this.sms || null;
    this.email = this.email || null;
  }
}

export type OfferInsertReference = {
  insertId: string;
  value: string;
}

export enum OfferStatus {
  Pending = 'pending',
  Distributed = 'distributed',
  Read = 'read',
  ReviewPending = 'reviewPending',
  Signed = 'signed',
  Canceled = 'canceled',
  Declined = 'declined',
  Expired = 'expired'
}

export function translateOfferStatus(status: OfferStatus): string {
  const translations = {
    [OfferStatus.Read]: text`Öppnat`,
    [OfferStatus.ReviewPending]: text`Behöver granskas`,
    [OfferStatus.Pending]: text`Ej skickat`,
    [OfferStatus.Distributed]: text`Skickat, oöppnat`,
    [OfferStatus.Signed]: text`Signerat`,
    [OfferStatus.Declined]: text`Avböjt`,
    [OfferStatus.Canceled]: text`Annullerat`,
    [OfferStatus.Expired]: text`Förfallit`,
  };

  return translations[status] || status;
}